import React from 'react';
import { Stack, TextField, Spinner, SpinnerSize, Image, ImageFit, IconButton, getTheme, Checkbox } from '@fluentui/react';
import { NGASearchResult } from './ArtGallery.state';
import { useEditedWidget } from 'features/Rooms/PersonalMeetingCorner/states/hooks';
import IvicosStrings from 'kits/language/stringKit';

const theme = getTheme();

interface ArtGalleryViewProps {
    searchQuery: string;
    setSearchQuery: (q: string) => void;
    searchNGA: (q: string) => void;
    ngaResults: NGASearchResult[];
    loading: boolean;
    selectedArtwork: NGASearchResult | null;
    setSelectedArtwork: (art: NGASearchResult | null) => void;
    preserveAspectRatio: boolean;
    setPreserveAspectRatio: (preserve: boolean) => void;
}

const ArtGalleryView: React.FC<ArtGalleryViewProps> = ({
    searchQuery,
    setSearchQuery,
    searchNGA,
    ngaResults,
    loading,
    selectedArtwork,
    setSelectedArtwork,
    preserveAspectRatio,
    setPreserveAspectRatio
}) => {
    const [editedWidget] = useEditedWidget();

    return (
        <Stack tokens={{ childrenGap: 20 }} style={{ margin: '10px 0px' }}>
            {!editedWidget?.content.imageUrl && (
                <TextField
                    placeholder={IvicosStrings.personalMeetingCornerStrings.artGallery.searchPlaceholder}
                    value={searchQuery}
                    onChange={(_, newValue) => {
                        setSearchQuery(newValue || '');
                        if (!newValue) {
                            setSelectedArtwork(null);
                        }
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && searchNGA(searchQuery)}
                    iconProps={{ iconName: 'Search' }}
                />
            )}

            {loading && <Spinner label={IvicosStrings.personalMeetingCornerStrings.artGallery.searchingCollections} size={SpinnerSize.small} />}

            {selectedArtwork && (
                <Stack style={{ position: 'relative' }}>
                    <Image src={selectedArtwork.imageUrl} alt={selectedArtwork.title} height={200} width="100%" imageFit={ImageFit.cover} />
                    <div style={{ fontSize: 12, fontWeight: 'bold' }}>{selectedArtwork.title}</div>
                    <div style={{ fontSize: 10 }}>{selectedArtwork.artist}</div>
                </Stack>
            )}

            {editedWidget && (
                <Stack>
                    <Image src={editedWidget.content.imageUrl} alt={editedWidget.content.title} height={250} width="100%" imageFit={ImageFit.cover} />
                    <div style={{ fontSize: 12, fontWeight: 'bold' }}>{editedWidget.content.title}</div>
                    <div style={{ fontSize: 10 }}>{editedWidget.content.artist}</div>
                </Stack>
            )}

            {ngaResults.length > 0 && (
                <Stack
                    horizontal
                    horizontalAlign="space-between"
                    wrap
                    tokens={{ childrenGap: 10 }}
                    style={{
                        height: 200,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        backgroundColor: theme.palette.neutralLighter,
                        padding: 10,
                        boxSizing: 'border-box',
                        boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)'
                    }}
                >
                    {ngaResults.map((artwork) => (
                        <Stack
                            key={artwork.id}
                            onClick={() => {
                                setSelectedArtwork(artwork);
                            }}
                            styles={{
                                root: {
                                    border: selectedArtwork?.id === artwork.id ? `2px solid ${theme.palette.themePrimary}` : '1px solid lightgray',
                                    backgroundColor: selectedArtwork?.id === artwork.id ? theme.palette.themeLighter : 'white',
                                    padding: 4,
                                    width: 150,
                                    cursor: 'pointer',
                                    borderRadius: 4,
                                    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)'
                                }
                            }}
                        >
                            <Image src={artwork.imageUrl} alt={artwork.title} height={80} width="100%" imageFit={ImageFit.cover} />
                            <div style={{ fontSize: 12, fontWeight: 'bold' }}>{artwork.title}</div>
                            <div style={{ fontSize: 10 }}>{artwork.artist}</div>
                        </Stack>
                    ))}
                </Stack>
            )}

            <Checkbox
                label={IvicosStrings.personalMeetingCornerStrings.createWidget.preserveAspectRatio}
                checked={preserveAspectRatio}
                onChange={(_, checked) => setPreserveAspectRatio(checked || false)}
            />
        </Stack>
    );
};

export default ArtGalleryView;
